import { useAppSelector } from "app/store";
import { Audit } from "models/generateAuditSchemas";
import { CustomAttributes } from "models/generateCustomAttributesSchema";
import { GetGaiaObjectResponse } from "pages/gaia-object-overview/definitions";
import { useEffect, useMemo } from "react";
import { useTeamManagement } from "./useTeamManagement";
import { useGetTeamByIdQuery } from "pages/team_settings/endpoints";
import { useTeamName } from "pages/team_settings/hooks";
import { z } from "zod";

/**
 * This hook does not update redux state, it's for loading custom attributes, schemas from state.
 *
 * @param category the gaiaObject category, it can be audit, notice, and litigation
 * @param data audit data or gaia object data from backend response
 * @param isDataLoading loading state for the data
 * @returns object containing customAttributes, createSchema, and formSchema
 */
export const useGaiaObjectCustomAttributes = (
  category: string,
  data?: Audit | GetGaiaObjectResponse,
  isDataLoading?: boolean
) => {
  // get current regular team setting from Redux
  const currentSelectedTeam = useAppSelector(
    (state) => state.teamSettings?.selectedTeamMainMetaData
  );

  // Get current guest team from Redux
  const currentGuestTeam = useAppSelector(
    (state) => state.teamSettings.currentGuestTeam
  );

  // Get all teams to check sub-team relationships
  const allTeams: any = useAppSelector((state) => state.teamSettings?.allTeams);

  // Get individual schemas
  const auditSchemas = useAppSelector(
    (state) => state.teamSettings.auditSchemas
  );
  const noticeSchemas = useAppSelector(
    (state) => state.teamSettings.noticeSchemas
  );
  const litigationSchemas = useAppSelector(
    (state) => state.teamSettings.litigationSchemas
  );

  // Get the team name from the data
  const teamNameInData = useMemo(() => {
    if (isDataLoading || !data) return undefined;
    return getTeamName(data);
  }, [data, isDataLoading]);

  const { currentResourceTeam, headerSelectedTeam } = useTeamName();

  // Determine final custom attributes and schemas
  const result = useMemo(() => {
    if (
      !headerSelectedTeam ||
      !currentResourceTeam ||
      (isDataLoading !== undefined && !teamNameInData)
    ) {
      return {
        customAttributes: undefined,
        createSchema: z.object({}),
        formSchema: z.object({}),
      };
    }

    let customAttributes;
    let schemas;

    if (headerSelectedTeam === "guest") {
      // when guest is chosen, we need to ensure currentGuestTeam teamId is same as teamNameInData and currentResourceTeam to ensure all states are synced before rendering the page
      if (
        !currentGuestTeam ||
        currentGuestTeam?.selectedTeamMainMetaData.teamId !== teamNameInData ||
        currentResourceTeam !== teamNameInData
      ) {
        return {
          customAttributes: undefined,
          createSchema: z.object({}),
          formSchema: z.object({}),
        };
      }
      customAttributes =
        currentGuestTeam.selectedTeamMainMetaData?.teamCustomAttributesSchema?.[
          category
        ];

      // Get schemas from guest team
      schemas = getSchemasByCategory(category, {
        auditSchemas: currentGuestTeam.auditSchemas,
        noticeSchemas: currentGuestTeam.noticeSchemas,
        litigationSchemas: currentGuestTeam.litigationSchemas,
      });
    } else {
      // if not guest, check header selected team matches team in data
      // Also allow sub-teams: if teamNameInData is a child of headerSelectedTeam
      const isSubTeamMatch =
        teamNameInData &&
        allTeams?.teams?.some(
          (team: any) =>
            team.teamId === teamNameInData &&
            team.parentTeamResourceId === headerSelectedTeam
        );
      const isSelectedTeamSubTeam =
        currentSelectedTeam.teamId &&
        allTeams?.teams?.some(
          (team: any) =>
            team.teamId === currentSelectedTeam.teamId &&
            team.parentTeamResourceId === headerSelectedTeam
        );
      if (
        (isDataLoading !== undefined &&
          headerSelectedTeam != teamNameInData &&
          !isSubTeamMatch) ||
        (headerSelectedTeam != currentSelectedTeam.teamId &&
          !isSelectedTeamSubTeam)
      ) {
        return {
          customAttributes: undefined,
          createSchema: z.object({}),
          formSchema: z.object({}),
        };
      }

      customAttributes =
        currentSelectedTeam.teamCustomAttributesSchema?.[category];

      // Get schemas from regular team
      schemas = getSchemasByCategory(category, {
        auditSchemas,
        noticeSchemas,
        litigationSchemas,
      });
    }

    return {
      customAttributes,
      createSchema: schemas.createSchema,
      formSchema: schemas.formSchema,
    };
  }, [
    data,
    isDataLoading,
    headerSelectedTeam,
    currentResourceTeam,
    teamNameInData,
    currentGuestTeam,
    currentSelectedTeam,
    category,
    auditSchemas,
    noticeSchemas,
    litigationSchemas,
  ]);

  return result;
};

/**
 * Helper function to get schemas by category
 */
function getSchemasByCategory(
  category: string,
  schemas: {
    auditSchemas?: any;
    noticeSchemas?: any;
    litigationSchemas?: any;
  }
): { createSchema: any; formSchema: any } {
  switch (category) {
    case "audit":
      return {
        createSchema: schemas.auditSchemas?.auditSchema || z.object({}),
        formSchema: schemas.auditSchemas?.auditFormSchema || z.object({}),
      };
    case "notice":
      return {
        createSchema: schemas.noticeSchemas?.gaiaObjSchema || z.object({}),
        formSchema: schemas.noticeSchemas?.gaiaObjFormSchema || z.object({}),
      };
    case "litigation":
      return {
        createSchema: schemas.litigationSchemas?.gaiaObjSchema || z.object({}),
        formSchema:
          schemas.litigationSchemas?.gaiaObjFormSchema || z.object({}),
      };
    default:
      return {
        createSchema: z.object({}),
        formSchema: z.object({}),
      };
  }
}

const getTeamName = (data?: Audit | GetGaiaObjectResponse) => {
  if (!data) {
    return undefined;
  }

  // If it's an Audit type (has direct teamName property)
  if ("teamName" in data) {
    return data.teamName;
  }

  // If it's a GetGaiaObjectResponse
  return data.gaiaObject?.teamName;
};
